"use client";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import styles from "./index.module.css";
import toast from "react-hot-toast";
import { useClient, useToken } from "../../lib/context/auth-context";
import { useSocket } from "./useSocket";
import { deleteComment, formatChatDate } from "../../lib/util";

import Pagination from "../common/Pagination";
import MediaViewer from "../common/MediaViewer";
import ChatMessage from "./ChatMessage";
import ChatAuth from "./ChatAuth";
import ChatSkeleton from "./ChatSkeleton";
import { chatIconWhite, retryArrow } from "../../assets";

const ChatArea = ({
  isChatOpen,
  readyForPagination,
  setReadyForPagination,
  chatAreaRef,
  savedScrollTop,
  state,
  setState,
  setReplyTo,
  setEditMessage,
  exposeScrollToMessage,
  onReplaceMessage,
  loggedInUser,
  handleRetry,
  chatPayload = {},
  chatInfo = {},
  markCommentsAsRead,
  setShowScrollToBottom,
  closeChat,
  chatBoxFetchError,
  activateGuestMode,
  chatGuestMode,
  handleBoxRetry,
  showSenderName = false,
}) => {
  const { userId } = chatPayload;
  const { sectionId, sectionName } = chatInfo;
  const client = useClient();

  const { token } = useToken();
  const { socket } = useSocket();

  const [firstLoad, setFirstLoad] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [activeMedia, setActiveMedia] = useState([]);
  const [showMediaViewer, setShowMediaViewer] = useState(false);
  const [onLoadStage, setOnLoadStage] = useState(1);
  const [isTyping, setIsTyping] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);

  const lastScrollTopRef = useRef(0);
  const chatBottomArea = useRef(null);
  const messageRefs = useRef({});
  const waitingRef = useRef(null);
  const pageRequestTimeout = useRef(null);
  const loadedPagesRef = useRef(new Set());
  const prevSectionRef = useRef(null);

  const queryEnabled = !!sectionId && !loadedPagesRef.current.has(state.page);

  const queryClient = useQueryClient();

  const {
    data: boxComments = {},
    isLoading: isLoadingComments,
    isFetching: isFetchingComments,
    isSuccess,
    error: commentsFetchError,
    refetch: refetchComments,
  } = useQuery(
    ["boxComments", sectionId, sectionName, state.page],
    async () => {
      const queryParams = [];

      queryParams.push(`limit=${state.limit}`);
      queryParams.push(`page=${state.page}`);
      const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";

      return client(
        `conversation/${sectionName}/${sectionId}/comments${queryString}`
      ).then((res) => res.data);
    },
    { enabled: queryEnabled, keepPreviousData: true }
  );

  const highlightAndScrollToEl = (el) => {
    const chatArea = chatAreaRef.current;
    if (!el || !chatArea) return;

    el.classList.add(styles.highlight);
    setTimeout(() => el.classList.remove(styles.highlight), 1200);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const elRect = el.getBoundingClientRect();
        const containerRect = chatArea.getBoundingClientRect();
        const offsetTop = elRect.top - containerRect.top + chatArea.scrollTop;

        const target = Math.max(
          0,
          Math.round(offsetTop - chatArea.clientHeight / 2 + elRect.height / 2)
        );

        chatArea.scrollTo({ top: target, behavior: "smooth" });
      });
    });
  };

  const scrollToMessage = (refId) => {
    if (!refId) return;
    const chatArea = chatAreaRef.current;
    if (!chatArea) return;

    const el = messageRefs.current[refId];
    if (el) {
      highlightAndScrollToEl(el);
      waitingRef.current = null;
      return;
    }
    waitingRef.current = refId;
    chatArea.scrollTop = 0;
  };

  const isNearBottom = () => {
    const chatArea = chatAreaRef.current;
    if (!chatArea) return false;
    const threshold = 80; // px tolerance
    return (
      chatArea.scrollHeight - chatArea.scrollTop - chatArea.clientHeight <
      threshold
    );
  };

  const handleCommentsRetry = useCallback(() => {
    if (refetchComments) {
      refetchComments();
    }
  }, [refetchComments]);

  const handleAuthSuccess = (user) => {
    if (user) {
      setShowAuth(false);
      activateGuestMode?.(false);
    }
  };

  const handleOpenMedia = (media) => {
    setShowMediaViewer(true);
    setActiveMedia(media);
  };

  const handleReply = (msg) => {
    setReplyTo(msg);
  };

  const handleCopy = (msg) => {
    if (!msg?.comment) {
      toast.error("Nothing to copy");
      return;
    }

    navigator?.clipboard
      .writeText(msg?.comment)
      .then(() => {
        toast.success("Message copied to clipboard");
      })
      .catch((err) => {
        toast.error("Failed to copy");
      });
  };

  const openPage = (requestedPage) => {
    if (pageRequestTimeout.current) {
      clearTimeout(pageRequestTimeout.current);
    }

    pageRequestTimeout.current = setTimeout(() => {
      setState((prev) => {
        const totalPages = prev.total
          ? Math.ceil(prev.total / prev.limit)
          : Infinity;
        const loaded = loadedPagesRef.current;

        let target = Math.max(1, Math.min(requestedPage, totalPages));

        // If already loaded, go forward until you find an unloaded page
        while (loaded.has(target) && target < totalPages) target++;

        if (loaded.has(target)) return prev; // no more to fetch

        return { ...prev, page: target };
      });
    }, 300);
  };

  function mergeMessages(existing = [], incoming = []) {
    const all = [...existing, ...incoming];

    const map = new Map();
    for (const msg of all) {
      if (!msg?.trackId) continue;
      map.set(msg.trackId, msg); // overwrite older entry with newer one
    }

    return Array.from(map.values()).sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );
  }

  useEffect(() => {
    const chatArea = chatAreaRef.current;
    if (!chatArea) return;
    if (!isSuccess) return;
    if (!sectionId || !boxComments) return;

    const { comments = [], limit, total, currentPage } = boxComments;

    // Save scroll position before updating
    const prevScrollHeight = chatArea.scrollHeight;
    const prevScrollTop = chatArea.scrollTop;

    if (prevScrollTop === 0) {
      chatArea.scrollTop = 50;
    }

    const normalized = [...comments].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    // Update state with new comments
    setState((prev) => ({
      ...prev,
      data: mergeMessages(prev.data, normalized),
      limit,
      total,
      currentPage,
    }));

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (chatArea) {
          if (firstLoad) {
            markCommentsAsRead();
            chatArea.scrollTop = chatArea.scrollHeight;
            setFirstLoad(false);
            setReadyForPagination(true);
          } else {
            const newScrollHeight = chatArea.scrollHeight;
            chatArea.scrollTop =
              prevScrollTop + (newScrollHeight - prevScrollHeight);
          }
        }
      });
    });
  }, [boxComments, sectionId, isSuccess]);

  useEffect(() => {
    if (isChatOpen && sectionId) {
      const cached = queryClient.getQueryData([
        "boxComments",
        sectionId,
        sectionName,
        state.page,
      ]);
      if (cached?.comments?.length) {
        setState((prev) => ({
          ...prev,
          data: mergeMessages(prev.data, cached.comments),
        }));
      }
      refetchComments(); // 🔹 ensures you still pull latest from API
    }
  }, [isChatOpen, sectionId, sectionName]);

  useEffect(() => {
    if (typeof exposeScrollToMessage === "function") {
      exposeScrollToMessage(scrollToMessage);
    }
  }, [exposeScrollToMessage]);

  useEffect(() => {
    const id = waitingRef.current;
    if (!id) return;

    const chatArea = chatAreaRef.current;
    const el = messageRefs.current[id];

    if (el) {
      highlightAndScrollToEl(el);
      waitingRef.current = null;
    } else {
      if (chatArea) chatArea.scrollTop = 0;
    }
  }, [state.data]);

  // Restore scroll position when reopening
  useLayoutEffect(() => {
    if (isChatOpen && chatAreaRef.current && savedScrollTop !== null) {
      const chatArea = chatAreaRef.current;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          chatArea.scrollTop = chatArea.scrollHeight - savedScrollTop;
          setTimeout(() => {
            setReadyForPagination(true);
            if (isNearBottom()) {
              markCommentsAsRead();
            }
          }, 100);
        });
      });
    }
  }, [isChatOpen, savedScrollTop]);

  useEffect(() => {
    const chatArea = chatAreaRef.current;
    if (!chatArea) return;

    let scrollTimeout;
    let isUserScrolling = false;

    const markUserScroll = () => {
      isUserScrolling = true;
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        isUserScrolling = false;
      }, 300); // reset after short idle
    };

    const handleScroll = () => {
      if (isNearBottom()) {
        setShowScrollToBottom(false);
        markCommentsAsRead();
      } else {
        setShowScrollToBottom(true);
      }

      // only blur if actively user-scrolling
      if (isUserScrolling) {
        const activeEl = document.activeElement;
        const currentTop = chatArea.scrollTop;
        if (Math.abs(currentTop - lastScrollTopRef.current) > 30) {
          if (activeEl && activeEl.tagName === "TEXTAREA") {
            activeEl.blur();
          }
        }
        lastScrollTopRef.current = currentTop;
      }

      // hide button after idle
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setShowScrollToBottom(false);
        isUserScrolling = false;
      }, 700);
    };

    // mark user intent from wheel/touch
    chatArea.addEventListener("wheel", markUserScroll, { passive: true });
    chatArea.addEventListener("touchmove", markUserScroll, { passive: true });
    chatArea.addEventListener("scroll", handleScroll);

    return () => {
      chatArea.removeEventListener("wheel", markUserScroll);
      chatArea.removeEventListener("touchmove", markUserScroll);
      chatArea.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  const handleDeleteComment = async (msg) => {
    const commentId = msg.trackId;
    // Optimistic remove
    setState((prev) => ({
      ...prev,
      data: prev.data.filter((m) => m.trackId !== commentId),
    }));

    try {
      await deleteComment(commentId, token);
    } catch (err) {
      toast.error("Failed to delete message");
      // Rollback
      setState((prev) => ({
        ...prev,
        data: [...prev.data, msg].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        ),
      }));
    }
  };

  useEffect(() => {
    if (socket && sectionId) {
      const channel = `${sectionName}:${sectionId}`;

      const handler = (res) => {
        const { action, data } = res || {};
        if (!data) return;

        if (action === "new_comment") {
          setState((prev) => ({
            ...prev,
            data: mergeMessages(prev.data, [data]),
          }));

          queryClient.setQueryData(
            ["boxComments", sectionId, sectionName, state.page],
            (old) => {
              if (!old) return { comments: [data] };
              return {
                ...old,
                comments: mergeMessages(old.comments || [], [data]),
              };
            }
          );

          if (isNearBottom()) {
            requestAnimationFrame(() => {
              chatAreaRef.current?.scrollTo({
                top: chatAreaRef.current.scrollHeight,
                behavior: "smooth",
              });
            });
          } else {
            setShowScrollToBottom(true);
          }
        }

        if (action === "edit_comment" || (data.commentEdit && !data.archive)) {
          onReplaceMessage(data.trackId, data);
          return;
        }

        if (action === "delete_comment" || data.archive) {
          handleDeleteComment(data);
          return;
        }

        if (action === "typing" && data.userId !== loggedInUser?.user.userId) {
          setIsTyping(true);
        }
        if (
          action === "stop-typing" &&
          data.userId !== loggedInUser?.user.userId
        ) {
          setIsTyping(false);
        }
      };

      socket.on(channel, handler);

      return () => {
        socket.off(channel, handler);
      };
    }
  }, [socket, sectionId, sectionName]);

  useEffect(() => {
    return () => {
      if (pageRequestTimeout.current) {
        clearTimeout(pageRequestTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!sectionId || !boxComments) return;
    const { currentPage } = boxComments;
    if (currentPage) loadedPagesRef.current.add(currentPage);
  }, [boxComments, sectionId]);

  useEffect(() => {
    if (sectionId && prevSectionRef.current !== sectionId) {
      loadedPagesRef.current = new Set();
      messageRefs.current = {};
      prevSectionRef.current = sectionId;
    }
  }, [sectionId]);

  const ordered = useMemo(() => {
    if (!state.data?.length) return [];
    return [...state.data].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );
  }, [state.data]);

  return (
    <>
      {chatGuestMode && !showAuth && (
        <div className={styles.guestBanner}>
          You’re chatting as a <strong> Guest</strong>.
          <button
            className={styles.loginCTA}
            onClick={() => {
              setShowAuth(true);
              setOnLoadStage(3);
            }}
          >
            Log in for more features
          </button>
        </div>
      )}
      <div className={styles.chatBody} ref={chatAreaRef}>
        {chatBoxFetchError || (commentsFetchError && firstLoad) ? (
          <div className={styles.errorState}>
            <p>Failed to load the chat window.</p>
            <button
              className={styles.retryBtn}
              onClick={() => {
                if (chatBoxFetchError) {
                  handleBoxRetry?.();
                } else {
                  handleCommentsRetry();
                }
              }}
            >
              <span className={styles.retryIcon}>{retryArrow}</span>
            </button>
          </div>
        ) : (!chatPayload?.userId && !chatGuestMode) || showAuth ? (
          <ChatAuth
            chatInfo={chatInfo}
            closeChat={closeChat}
            activateGuestMode={activateGuestMode}
            handleAuthSuccess={handleAuthSuccess}
            onLoadStage={onLoadStage}
          />
        ) : (
          <>
            {!isLoadingComments && !firstLoad && state.data?.length === 0 && (
              <div
                className={styles.emptyState}
                role="status"
                aria-live="polite"
              >
                <div className={styles.emptyText}>
                  Hi, {loggedInUser?.user?.firstName || "there"}
                </div>
                <div className={styles.emptyHint}>
                  Start the conversation with an admin in{" "}
                  <strong>{chatInfo?.name}</strong>
                </div>

                <button
                  className={styles.startChatBtn}
                  onClick={() => {
                    // Hook into your logic to open the composer or focus the input
                    const input = document.querySelector("#chatMessage");
                    if (input) input.focus();
                  }}
                >
                  Start Chat{" "}
                  <span className={styles.emptyIcon} aria-hidden="true">
                    {chatIconWhite}
                  </span>
                </button>
              </div>
            )}

            {(isLoadingComments || firstLoad) && <ChatSkeleton />}

            {state.data && state.data?.length > 0 && (
              <>
                <Pagination
                  infiniteScroll={true}
                  total={state.total}
                  limit={state.limit}
                  page={state.page}
                  onPageClick={openPage}
                  enableManualMode={true}
                  autoLoadThreshold={15}
                  isLoading={isLoadingComments || firstLoad}
                  isFetching={isFetchingComments}
                  readyForPagination={readyForPagination}
                  showEndOfList={true}
                  endOfListText="🎉 You've reached the end!"
                />

                <div className={styles.commentArea}>
                  {ordered?.map((msg, index) => {
                    const msgDate = new Date(msg.createdAt);
                    const prevMsg = ordered[index - 1];
                    const prevDate = prevMsg
                      ? new Date(prevMsg.createdAt)
                      : null;

                    const showDateSeparator =
                      !prevDate ||
                      msgDate.toDateString() !== prevDate.toDateString();

                    return (
                      <div
                        key={msg.trackId || msg._id || index}
                        data-id={msg.trackId}
                        ref={(el) => {
                          if (el) messageRefs.current[msg.trackId] = el;
                          else delete messageRefs.current[msg.trackId];
                        }}
                      >
                        {showDateSeparator && (
                          <div className={styles.dateSeparator}>
                            <div className={styles.line}></div>
                            <span className={styles.dateText}>
                              {formatChatDate(msg.createdAt)}
                            </span>
                            <div className={styles.line}></div>
                          </div>
                        )}
                        <ChatMessage
                          showSenderName={showSenderName}
                          currentUserId={userId}
                          chatAreaRef={chatAreaRef}
                          message={msg}
                          highlightedMessageId={highlightedMessageId}
                          setHighlightedMessageId={setHighlightedMessageId}
                          scrollToMessage={scrollToMessage}
                          handleOpenMedia={handleOpenMedia}
                          handleDelete={handleDeleteComment}
                          handleCopy={handleCopy}
                          handleReply={handleReply}
                          handleRetry={handleRetry}
                          handleEdit={(msg) => {
                            setReplyTo(null);
                            setEditMessage({
                              trackId: msg.trackId,
                              text: msg.comment,
                            });
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <div ref={chatBottomArea}></div>
            {isTyping && (
              <div
                className={`${styles.messageRow} ${styles.theirs}`}
                style={{ paddingLeft: "2rem" }}
              >
                <div className={styles.joinDiv}>
                  <div className={`${styles.bubble} ${styles.typingBubble}`}>
                    <div className={styles.typingBubbleDots}>
                      <span className={styles.dot}></span>
                      <span className={styles.dot}></span>
                      <span className={styles.dot}></span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {showMediaViewer && (
        <MediaViewer
          media={activeMedia}
          setShowMediaViewer={setShowMediaViewer}
        />
      )}
    </>
  );
};

export default ChatArea;
